import React, { useState } from "react";
import CustomButton from "../../components/Admin/CustomButton/CustomButton";
import { TerminalButtonSettings } from "../../types/Admin/TerminalAppearanceTypes";
import { normalizeMediaUrl } from "../../utils/mediaBackground";

const getMediaBackgroundSize = (fitMode: string | null | undefined, scale: number) => {
  switch (fitMode) {
    case "contain":
      return "contain";
    case "cover":
      return "cover";
    case "stretch":
      return "100% 100%";
    case "width":
      return "100% auto";
    case "height":
      return "auto 100%";
    case "none":
      return "auto";
    case "scale":
    default:
      return `${scale}% ${scale}%`;
  }
};
import classes from "./TerminalAppearancePage.module.scss";

const defaultButtonGradient = {
  startColor: "#0066ff",
  endColor: "#56c5f2",
  angle: 180,
};

const fontOptions = [
  { label: "SFPro", value: "SFPro" },
  { label: "Inter", value: "Inter" },
  { label: "Montserrat", value: "Montserrat" },
  { label: "Roboto Regular", value: "Roboto-400" },
  { label: "Roboto Medium", value: "Roboto" },
  { label: "Bebas", value: "Bebas" },
];

const fontWeights = [
  { label: "Regular", value: "regular" },
  { label: "Medium", value: "medium" },
  { label: "Bold", value: "bold" },
];

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const normalizeHexColor = (value: string, fallback = "#000000") => {
  const cleaned = value.trim().replace("#", "");
  if (![3, 6].includes(cleaned.length) || !/^[0-9a-fA-F]+$/.test(cleaned)) {
    return fallback;
  }
  const full =
    cleaned.length === 3
      ? cleaned
          .split("")
          .map((char) => char + char)
          .join("")
      : cleaned;
  return `#${full.toLowerCase()}`;
};

const hexToRgb = (hex: string) => {
  const raw = normalizeHexColor(hex).replace("#", "");
  return {
    r: parseInt(raw.slice(0, 2), 16),
    g: parseInt(raw.slice(2, 4), 16),
    b: parseInt(raw.slice(4, 6), 16),
  };
};

const parseColorWithAlpha = (value?: string | null, fallbackHex = "#000000") => {
  if (!value) {
    return { hex: fallbackHex, alpha: 1 };
  }
  const trimmed = value.trim();
  if (trimmed.startsWith("#")) {
    return { hex: normalizeHexColor(trimmed, fallbackHex), alpha: 1 };
  }
  const rgbaMatch = trimmed.match(/rgba?\(([^)]+)\)/i);
  if (rgbaMatch) {
    const parts = rgbaMatch[1].split(",").map((part) => part.trim());
    const r = Number(parts[0]);
    const g = Number(parts[1]);
    const b = Number(parts[2]);
    if (![r, g, b].every((channel) => Number.isFinite(channel))) {
      return { hex: fallbackHex, alpha: 1 };
    }
    const hex = `#${[r, g, b]
      .map((channel) =>
        clampNumber(Math.round(channel), 0, 255).toString(16).padStart(2, "0"),
      )
      .join("")}`;
    const alpha = parts.length > 3 ? clampNumber(Number(parts[3]), 0, 1) : 1;
    return {
      hex: normalizeHexColor(hex, fallbackHex),
      alpha: Number.isFinite(alpha) ? alpha : 1,
    };
  }
  return { hex: fallbackHex, alpha: 1 };
};

const buildColorWithAlpha = (hex: string, alpha: number) => {
  const normalizedHex = normalizeHexColor(hex);
  const clamped = clampNumber(alpha, 0, 1);
  if (clamped >= 1) {
    return normalizedHex;
  }
  const { r, g, b } = hexToRgb(normalizedHex);
  const roundedAlpha = Math.round(clamped * 100) / 100;
  return `rgba(${r}, ${g}, ${b}, ${roundedAlpha})`;
};

const parseGradient = (gradient?: string | null) => {
  if (!gradient) {
    return defaultButtonGradient;
  }
  const angleMatch = gradient.match(/linear-gradient\(([-\d.]+)deg/i);
  const angle = angleMatch ? Number(angleMatch[1]) : defaultButtonGradient.angle;
  const colorMatches =
    gradient.match(/#(?:[0-9a-fA-F]{3}){1,2}|rgba?\([^)]+\)/g) ?? [];
  const [
    startColor = defaultButtonGradient.startColor,
    endColor = defaultButtonGradient.endColor,
  ] = colorMatches;
  return {
    startColor,
    endColor,
    angle: Number.isFinite(angle) ? angle : defaultButtonGradient.angle,
  };
};

const buildGradient = (settings: typeof defaultButtonGradient) =>
  `linear-gradient(${settings.angle}deg, ${settings.startColor} 0%, ${settings.endColor} 100%)`;

const parseNumberInput = (value: string) => {
  if (!value.trim()) return Number.NaN;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

const toNumberInputValue = (value?: number | null): string | number =>
  typeof value === "number" && Number.isFinite(value) ? value : "";

const toFiniteNumber = (value: number | null | undefined, fallback: number) =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

type CitoButtonSettingsProps = {
  buttons: TerminalButtonSettings;
  isUploading: boolean;
  onChange: (
    key: keyof TerminalButtonSettings,
    value: string | boolean | number | null,
  ) => void;
  onUpload: (file: File) => void;
  onRemoveIcon: () => void;
};

const CitoButtonSettings: React.FC<CitoButtonSettingsProps> = ({
  buttons,
  isUploading,
  onChange,
  onUpload,
  onRemoveIcon,
}) => {
  const [groupsExpanded, setGroupsExpanded] = useState({
    icon: false,
    fill: false,
    font: false,
  });
  const gradient = parseGradient(buttons.gradient);
  const gradientStartColor = parseColorWithAlpha(
    gradient.startColor,
    defaultButtonGradient.startColor,
  );
  const gradientEndColor = parseColorWithAlpha(
    gradient.endColor,
    defaultButtonGradient.endColor,
  );
  const textColor = parseColorWithAlpha(buttons.textColor ?? "#ffffff", "#ffffff");
  const previewUrl = normalizeMediaUrl(buttons.ticketOuterCircleSmallUrl);
  const fitMode = buttons.ticketOuterCircleSmallFitMode ?? "scale";
  const scaleValue = toFiniteNumber(buttons.ticketOuterCircleSmallScale, 100);
  const positionXValue = toFiniteNumber(buttons.ticketOuterCircleSmallPositionX, 50);
  const positionYValue = toFiniteNumber(buttons.ticketOuterCircleSmallPositionY, 50);

  const toggleGroup = (key: keyof typeof groupsExpanded) => {
    setGroupsExpanded((current) => ({ ...current, [key]: !current[key] }));
  };

  const updateGradient = (partial: Partial<typeof defaultButtonGradient>) => {
    onChange("gradient", buildGradient({ ...gradient, ...partial }));
  };

  return (
    <div className={classes.buttonSettingsGroups}>
      <div className={classes.buttonSettingsGroup}>
        <button
          type="button"
          className={classes.buttonSettingsHeader}
          onClick={() => toggleGroup("icon")}
        >
          <span className={classes.buttonSettingsTitle}>Иконка кнопки</span>
          <span
            className={`${classes.collapseIcon} ${
              groupsExpanded.icon ? classes.collapseIconExpanded : ""
            }`}
            aria-hidden="true"
          >
            {groupsExpanded.icon ? "▲" : "▼"}
          </span>
        </button>
        {groupsExpanded.icon && (
          <div className={classes.buttonSettingsBody}>
            <div className={classes.buttonMediaUpload}>
              <div className={classes.logoUploadRow}>
                <label htmlFor="cito-outer-circle-small-upload">
                  Иконка(jpg, jpeg, png, webp, gif, svg)
                </label>
                <div className={classes.logoUploadInputRow}>
                  <input
                    id="cito-outer-circle-small-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) onUpload(file);
                      event.target.value = "";
                    }}
                  />
                  {previewUrl && (
                    <CustomButton
                      content="Удалить"
                      styleType="default"
                      onClick={onRemoveIcon}
                    />
                  )}
                </div>
              </div>
              {isUploading && <span>Загрузка...</span>}
              <div className={classes.buttonMediaControls}>
                <label>
                  Режим отображения
                  <select
                    className={classes.displayModeSelect}
                    value={fitMode}
                    onChange={(event) =>
                      onChange("ticketOuterCircleSmallFitMode", event.target.value)
                    }
                  >
                    <option value="scale">Свой размер</option>
                    <option value="contain">Вписать в область</option>
                    <option value="cover">Заполнить область</option>
                    <option value="stretch">Растянуть (по ширине и высоте)</option>
                    <option value="width">Растянуть по ширине</option>
                    <option value="height">Растянуть по высоте</option>
                    <option value="none">Оригинальный размер</option>
                  </select>
                </label>
                <label className={classes.rangeField}>
                  Масштаб
                  <div className={classes.rangeRow}>
                    <input
                      type="range"
                      min={50}
                      max={200}
                      step={1}
                      value={scaleValue}
                      disabled={fitMode !== "scale"}
                      onChange={(event) =>
                        onChange(
                          "ticketOuterCircleSmallScale",
                          Number(event.target.value),
                        )
                      }
                    />
                    <input
                      type="number"
                      min={50}
                      max={200}
                      value={toNumberInputValue(buttons.ticketOuterCircleSmallScale)}
                      disabled={fitMode !== "scale"}
                      onChange={(event) =>
                        onChange(
                          "ticketOuterCircleSmallScale",
                          parseNumberInput(event.target.value),
                        )
                      }
                    />
                    <span>%</span>
                  </div>
                </label>
                <label className={classes.rangeField}>
                  Позиция по X
                  <div className={classes.rangeRow}>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={positionXValue}
                      onChange={(event) =>
                        onChange(
                          "ticketOuterCircleSmallPositionX",
                          Number(event.target.value),
                        )
                      }
                    />
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={toNumberInputValue(
                        buttons.ticketOuterCircleSmallPositionX,
                      )}
                      onChange={(event) =>
                        onChange(
                          "ticketOuterCircleSmallPositionX",
                          parseNumberInput(event.target.value),
                        )
                      }
                    />
                    <span>%</span>
                  </div>
                </label>
                <label className={classes.rangeField}>
                  Позиция по Y
                  <div className={classes.rangeRow}>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={positionYValue}
                      onChange={(event) =>
                        onChange(
                          "ticketOuterCircleSmallPositionY",
                          Number(event.target.value),
                        )
                      }
                    />
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={toNumberInputValue(
                        buttons.ticketOuterCircleSmallPositionY,
                      )}
                      onChange={(event) =>
                        onChange(
                          "ticketOuterCircleSmallPositionY",
                          parseNumberInput(event.target.value),
                        )
                      }
                    />
                    <span>%</span>
                  </div>
                </label>
              </div>
              {previewUrl && (
                <div className={classes.buttonMediaCard}>
                  <div className={classes.buttonMediaPreview}>
                    <div
                      className={classes.buttonMediaPreviewImage}
                      style={{
                        backgroundImage: `url(${previewUrl})`,
                        backgroundSize: getMediaBackgroundSize(fitMode, scaleValue),
                        backgroundPosition: `${positionXValue}% ${positionYValue}%`,
                      }}
                      aria-label="Предпросмотр"
                    />
                  </div>
                </div>
              )}
            </div>
            <label className={classes.checkboxRow}>
              <input
                type="checkbox"
                checked={buttons.hideOuterCircleSmall}
                onChange={(event) =>
                  onChange("hideOuterCircleSmall", event.target.checked)
                }
              />
              Скрыть
            </label>
          </div>
        )}
      </div>

      <div className={classes.buttonSettingsGroup}>
        <button
          type="button"
          className={classes.buttonSettingsHeader}
          onClick={() => toggleGroup("fill")}
        >
          <span className={classes.buttonSettingsTitle}>Заливка кнопки</span>
          <span
            className={`${classes.collapseIcon} ${
              groupsExpanded.fill ? classes.collapseIconExpanded : ""
            }`}
            aria-hidden="true"
          >
            {groupsExpanded.fill ? "▲" : "▼"}
          </span>
        </button>
        {groupsExpanded.fill && (
          <div className={classes.buttonSettingsBody}>
            <div className={classes.gradientControlGroup}>
              <span className={classes.gradientLabel}>Градиент заливки</span>
              <div className={classes.gradientRow}>
                <div className={classes.gradientField}>
                  <span>Цвет 1</span>
                  <input
                    type="color"
                    value={gradientStartColor.hex}
                    onChange={(event) =>
                      updateGradient({
                        startColor: buildColorWithAlpha(
                          event.target.value,
                          gradientStartColor.alpha,
                        ),
                      })
                    }
                  />
                  <span>Непрозрачность</span>
                  <div className={classes.rangeRow}>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={Math.round(gradientStartColor.alpha * 100)}
                      onChange={(event) =>
                        updateGradient({
                          startColor: buildColorWithAlpha(
                            gradientStartColor.hex,
                            clampNumber(Number(event.target.value), 0, 100) / 100,
                          ),
                        })
                      }
                    />
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={Math.round(gradientStartColor.alpha * 100)}
                      onChange={(event) =>
                        updateGradient({
                          startColor: buildColorWithAlpha(
                            gradientStartColor.hex,
                            clampNumber(Number(event.target.value), 0, 100) / 100,
                          ),
                        })
                      }
                    />
                    <span>%</span>
                  </div>
                </div>
                <div className={classes.gradientField}>
                  <span>Цвет 2</span>
                  <input
                    type="color"
                    value={gradientEndColor.hex}
                    onChange={(event) =>
                      updateGradient({
                        endColor: buildColorWithAlpha(
                          event.target.value,
                          gradientEndColor.alpha,
                        ),
                      })
                    }
                  />
                  <span>Непрозрачность</span>
                  <div className={classes.rangeRow}>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={Math.round(gradientEndColor.alpha * 100)}
                      onChange={(event) =>
                        updateGradient({
                          endColor: buildColorWithAlpha(
                            gradientEndColor.hex,
                            clampNumber(Number(event.target.value), 0, 100) / 100,
                          ),
                        })
                      }
                    />
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={Math.round(gradientEndColor.alpha * 100)}
                      onChange={(event) =>
                        updateGradient({
                          endColor: buildColorWithAlpha(
                            gradientEndColor.hex,
                            clampNumber(Number(event.target.value), 0, 100) / 100,
                          ),
                        })
                      }
                    />
                    <span>%</span>
                  </div>
                </div>
                <label className={classes.gradientField}>
                  Угол
                  <div className={classes.gradientAngle}>
                    <input
                      type="range"
                      min={0}
                      max={360}
                      step={1}
                      value={gradient.angle}
                      onChange={(event) =>
                        updateGradient({ angle: Number(event.target.value) })
                      }
                    />
                    <span className={classes.gradientAngleValue}>
                      {gradient.angle}°
                    </span>
                  </div>
                </label>
              </div>
              <div
                className={classes.gradientPreview}
                style={{ background: buttons.gradient }}
              />
            </div>
          </div>
        )}
      </div>

      <div className={classes.buttonSettingsGroup}>
        <button
          type="button"
          className={classes.buttonSettingsHeader}
          onClick={() => toggleGroup("font")}
        >
          <span className={classes.buttonSettingsTitle}>Шрифт кнопки</span>
          <span
            className={`${classes.collapseIcon} ${
              groupsExpanded.font ? classes.collapseIconExpanded : ""
            }`}
            aria-hidden="true"
          >
            {groupsExpanded.font ? "▲" : "▼"}
          </span>
        </button>
        {groupsExpanded.font && (
          <div className={classes.buttonSettingsBody}>
            <div className={classes.grid}>
              <div className={classes.gradientField}>
                <span>Цвет текста</span>
                <input
                  type="color"
                  value={textColor.hex}
                  onChange={(event) =>
                    onChange(
                      "textColor",
                      buildColorWithAlpha(event.target.value, textColor.alpha),
                    )
                  }
                />
                <span>Непрозрачность</span>
                <div className={classes.rangeRow}>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={Math.round(textColor.alpha * 100)}
                    onChange={(event) =>
                      onChange(
                        "textColor",
                        buildColorWithAlpha(
                          textColor.hex,
                          clampNumber(Number(event.target.value), 0, 100) / 100,
                        ),
                      )
                    }
                  />
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={Math.round(textColor.alpha * 100)}
                    onChange={(event) =>
                      onChange(
                        "textColor",
                        buildColorWithAlpha(
                          textColor.hex,
                          clampNumber(Number(event.target.value), 0, 100) / 100,
                        ),
                      )
                    }
                  />
                  <span>%</span>
                </div>
              </div>
              <label>
                Шрифт
                <select
                  value={buttons.fontFamily}
                  onChange={(event) => onChange("fontFamily", event.target.value)}
                >
                  {fontOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Начертание
                <select
                  value={buttons.fontWeight}
                  onChange={(event) => onChange("fontWeight", event.target.value)}
                >
                  {fontWeights.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Размер шрифта
                <input
                  type="number"
                  value={toNumberInputValue(buttons.fontSize)}
                  onChange={(event) =>
                    onChange("fontSize", parseNumberInput(event.target.value))
                  }
                />
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CitoButtonSettings;
